import { Children, type ReactNode } from 'react'

import { Alert } from '../ui/Alert'
import { Button } from '../ui/Button'
import { ToastList, useVisibleToasts } from './ToastList'
import { dismissNotice, useRegisterRegion, useVisibleNotices } from './notificationStore'

/**
 * LA ZONE DE NOTIFICATION (LOT-H9, DA-002).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * UNE ZONE QUI POUSSE LE CONTENU, PAS UNE PILE QUI SE POSE DESSUS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * > « Pile de notifications dans une zone dédiée non superposable (sous le
 * >   header, poussant le contenu), jamais en `position: fixed` au-dessus d'une
 * >   zone d'action. » — recette DA, correctif de DA-002
 *
 * C'est la lettre du correctif, et c'est aussi la seule forme qui tienne la
 * promesse : tant qu'une pile est en `position: fixed`, il existe toujours un
 * écran où elle se pose sur quelque chose de cliquable. H0 l'avait déjà
 * déplacée sous l'en-tête ; la recette a mesuré qu'elle recouvrait alors le
 * cluster d'actions du `PageHeader`, filtre de campagne compris — deux boutons
 * inatteignables. Dans le flux, la question ne se pose plus.
 *
 * Le prix est assumé : quand un message arrive, le contenu descend de la
 * hauteur du bandeau. C'est visible, et c'est préférable à une bulle qui masque
 * l'action qu'on allait déclencher.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ELLE N'EXISTE QUE QUAND ELLE A QUELQUE CHOSE À DIRE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Zone vide ⇒ **aucun élément rendu**, donc aucune marge. L'anatomie verticale
 * de RT-DA1 (§3bis) est intacte : `pt-4` du `<main>` et rien d'autre au-dessus
 * du titre. Avec un bandeau, le `mb-2` de la zone porte le vide à 24 px —
 * exactement ce que le bandeau micro produisait déjà, et les deux bornes
 * restent dans la cible 16–24 px de la recette.
 *
 * Trois familles y cohabitent, dans cet ordre de priorité :
 *   1. les bandeaux du CHÂSSIS (`children`) — poste téléphonique en panne ;
 *   2. les états PERSISTANTS déclarés par l'écran (`notificationStore`) ;
 *   3. les messages ÉPHÉMÈRES (`react-hot-toast`), les plus bas car les plus
 *      volatils : ils ne doivent pas faire sauter les deux autres à chaque
 *      apparition.
 */

export interface NotificationRegionProps {
  /** Bandeaux du châssis. `false` / `null` comptent pour rien (zone vide). */
  children?: ReactNode
}

export function NotificationRegion({ children }: NotificationRegionProps) {
  useRegisterRegion()

  const notices = useVisibleNotices()
  const toasts = useVisibleToasts()

  // `Children.toArray` écarte `null`, `undefined` et les booléens : un
  // `{condition && <Alert/>}` faux ne fait pas exister la zone.
  const shellBanners = Children.toArray(children)

  if (shellBanners.length === 0 && notices.length === 0 && toasts.length === 0) return null

  return (
    <div
      data-testid="notification-region"
      // Mêmes gouttières que le bandeau qu'elle remplace : l'anatomie ne bouge pas.
      className="mx-4 mb-2 flex flex-col gap-2 lg:mx-8"
    >
      {shellBanners}

      {notices.map((notice) => (
        <Alert
          key={notice.id}
          tone={notice.tone}
          title={notice.title}
          onDismiss={() => dismissNotice(notice.id)}
          action={
            notice.action && (
              <Button variant="secondary" size="sm" onClick={notice.action.onClick}>
                {notice.action.label}
              </Button>
            )
          }
        >
          {notice.description}
        </Alert>
      ))}

      <ToastList toasts={toasts} surface="inline" />
    </div>
  )
}

export default NotificationRegion
